
import axios from 'axios'
import EoButton from '../EoButton/EoButton'
import EoIcon from '../EoIcon/EoIcon'
import EoInput from '../EoInput/EoInput'
import EoList from '../EoList/EoList'
import EoTransitionAccordion from '../EoTransitionAccordion/EoTransitionAccordion'

import EoSearchFilterChips from './EoSearchFilterChips'
import EoSearchFilters from './EoSearchFilters'
import EoSearchResult from './EoSearchResult'

// TODO: Use microservice / dedicated search api endpoint via EO API instead of SS360
const SS360_API_ENDPOINT = 'https://api.sitesearch360.com/sites?&site=eo.nl'

const LIMIT = 8

const FILTER_MAP = {
  arrangement: 'Arrangement',
  bundle: 'Bundel',
  category: 'Categorie',
  church: 'Kerk',
  community: 'Community',
  composer: 'Componist',
  conductor: 'Dirigent',
  genre: 'Genre',
  girlBoyBiblenames: 'Bijbelsenamen meisje jongen',
  languageBiblenames: 'Bijbelsenamen taal',
  performer: 'Uitvoerenden',
  popularBiblenamens: 'Populaire bijbelsenamen',
  subject: 'Onderwerp',
  testamentBiblenames: 'Bijbelsenamen testament',
  topic: 'Thema',
  type: 'Type',
}

const SS360_FILTER_MAP = {
  genre: 'fid#1',
  topic: 'fid#2',
  category: 'fid#3',
  community: 'fid#4',
  type: 'fid#5',
  popularBiblenamens: 'fid#6',
  girlBoyBiblenames: 'fid#7',
  languageBiblenames: 'fid#8',
  testamentBiblenames: 'fid#9',
  arrangement: 'fid#10',
  bundle: 'fid#11',
  church: 'fid#12',
  composer: 'fid#13',
  conductor: 'fid#14',
  performer: 'fid#15',
  subject: 'fid#16',
}

export default {
  name: 'EoSearch',
  components: {
    EoButton,
    EoIcon,
    EoInput,
    EoList,
    EoTransitionAccordion,
    EoSearchFilterChips,
    EoSearchFilters,
    EoSearchResult,
  },
  props: {
    /**
     * Site Search 360: site ID to search in
     */
    siteId: {
      type: String,
      default: 'eo.nl',
    },
    /**
     * Site Search 360: content groups to include in the search
     */
    contentGroups: {
      type: Array,
      default: () => [],
    },
    darkMode: {
      type: Boolean,
      default: false,
    },
    /**
     * URI parameter for the keyword
     */
    keywordParameter: {
      type: String,
      default: 'invoer',
    },
    /**
     * Applied filters for this component
     */
    appliedFilters: {
      type: Array,
      default: () => [
        'genre',
        'topic',
        'category',
        'community',
        'popularBiblenamens',
        'girlBoyBiblenames',
        'languageBiblenames',
        'testamentBiblenames',
      ],
    },
    /*
     * Style: color used on buttons
     */
    buttonColor: {
      type: String,
      default: null,
    },
  },
  data () {
    return {
      apiData: {},
      results: [],
      filters: [],
      hasSearched: false,
      isFilterDrawerOpen: false,
      isSearching: false,
      keyword: null,
      queryCorrection: null,
      suggestGroups: {},
      timesLoadedMore: 0,
    }
  },
  computed: {
    resultsCount () {
      return this.apiData?.totalResults
    },
    hasResults () {
      return this.results && this.results.length > 0
    },
    hasMoreResults () {
      return this.results && this.results.length < this.resultsCount
    },
    hasKeyword () {
      return this.keyword !== null && this.keyword !== ""
    },
    hasFilters () {
      return this.filters && Object.keys(this.filters).length
    },
    hasSelectedFilters () {
      if (!this.hasFilters) return false
      return Object.values(this.filters).some(filter => {
        return filter.selected && filter.selected.length
      })
    },
  },
  created () {
    // Init data from URI
    if (this.$route) {
      const { query = {} } = this.$route
      const {[this.keywordParameter]: keywordParam, ...filterQueries} = query
      this.keyword = keywordParam
      this.filters = this.buildFiltersFromQuery(filterQueries)
    }

    // Immediatly start searching if there is a keyword in the URI
    if (this.keyword) {
      this.querySS360(this.keyword)
    }

    // Add watchers
    this.$watch('filters', this.onChangeFilters, { deep: true })
    this.$watch('apiData', this.onChangeApiData, { deep: true })
    this.$watch('$route', this.onChangeRoute)
  },
  mounted () {
    if (!this.keyword) {
      this.$refs.input.focus()
    }
  },
  methods: {
    /**
     * Search API data methods
     */
    async querySS360 (keyword, isLoadMore = false) {
      if (process.server || keyword === 'undefined' || !keyword || this.isSearching ) { return null }
      this.isSearching = true
      this.timesLoadedMore += isLoadMore ? 1 : 0

      // Update the Uri of the page, so it corresponds to this search query
      if (this.$router) {
        const filters = this.filters.reduce((acc, filter) => {
          if (filter.selected !== null) {
            const selectedFilter = {[(filter.key)]: filter.selected}
            return {...acc, ...selectedFilter}
          } else {
            return acc
          }
        }, {})
        const keyword = this.keyword ? {[this.keywordParameter]: this.keyword} : {}
        const query = {...keyword, ...filters}
        if (!this.isCurrentQuery(query)) {
          this.$router.replace({query: {...query}})
        }
      }

      // Create object of the selected filters
      const currentFilters = this.filters
      const selectedFilters = currentFilters.reduce((acc, filter) => {
        if (filter.selected !== null) {
          const selection = filter.selected.reduce((acc, selected) => {
            return [...acc, ...[{name: selected}]]
          }, [])
          const selectedFilter = {
            key: SS360_FILTER_MAP[filter.key],
            values: selection,
          }
          return [...acc, ...[selectedFilter]]
        } else {
          return acc
        }
      }, [])

      // Send query to SS360
      const { data: apiData } = await axios.get(SS360_API_ENDPOINT, {
        params: {
          complexFilterFormat: true,
          filterOptions: true,
          filters: JSON.stringify(selectedFilters ? selectedFilters : null), // Example: {"key":"fid#1","values":[{"name":"Jeugd"}]}
          includeContent: true,
          includeContentGroups: JSON.stringify(this.contentGroups),
          kvtable: false,
          limit: LIMIT,
          offset: isLoadMore ? this.timesLoadedMore * LIMIT : 0,
          order: 'DESC',
          query: keyword,
          sort: 'Publicatiedatum',
        },
      })

      // Apply new data from SS360 response
      this.hasSearched = true
      this.isSearching = false
      if (!isLoadMore) {
        this.results = []
      }
      Object.values(apiData.suggests).forEach(result => this.results.push(...result))
      delete apiData.suggests
      this.apiData = apiData

      // Track event in Google Tag Manager
      if (this.$gtm) {
        this.$gtm.push({
          event: 'eo_search',
          search_term: keyword,
        })
      }
    },
    getDatapoint(suggest, key) {
      const a = suggest.dataPoints.find(item => {
        return item.key === key
      })
      return a ? a.value : ''
    },
    clearSearch () {
      this.apiData = {}
      this.filters = []
      this.hasSearched = false
      this.keyword = null
      this.timesLoadedMore = 0
      this.$router.replace({query: {}})
    },

    /**
     * Filter methods
     */
    buildFilters () {
      // Create indexed filters
      const { filterOptions: filterOptions = [] } = this.apiData
      const filters = filterOptions.reduce((acc, filter) => {
        const key = this.reverseMap(SS360_FILTER_MAP)[filter.key] || filter.name.toLowerCase()
        const name = filter.name
        const options = filter.values.reduce((acc, option) => {
          const optionLabel =  option
          const optionSlug = option
          const count = filter.counts[option]
          return [...acc, ...[this.castToFilterOption(optionLabel, optionSlug, count)]]
        }, [])

        // Find the selection of the filter and return the selection from SS360
        let selection = null
        if (this.apiData) {
          const activeFilterOptions = this.apiData.activeFilterOptions.reduce((acc, filter) => {
            const values = filter?.values.reduce((acc, value) => {
              return [...acc, ...[value.key]]
            }, [])
            const map = this.reverseMap(SS360_FILTER_MAP)
            return {...acc, ...{[map[filter.key]]: values}}
          }, {})
          selection = activeFilterOptions[key] // Example: {"genre": ["Jeugd", "Film"]}
        }
        return [...acc, ...[this.castToFilter(key, name, options, selection)]]
      }, [])
      return this.stripUnusedFilters([...filters])
    },
    buildFiltersFromQuery (query) {
      // Create indexed filters from query
      const filters = Object.entries(query).reduce((acc, [ key, value ]) => {
        const options = [value].flat() // TypeCast to Array: Value can be a string or an array

        if (key in FILTER_MAP) {
          const filterOptions = options.reduce((acc, option) => {
            return [...acc, ...[this.castToFilterOption(option, option, 0)]]
          }, [])
          const filter = this.castToFilter(key, FILTER_MAP[key], filterOptions, options)
          return [...acc, ...[filter]]
        } else {
          return [...acc]
        }
      }, [])
      return this.stripUnusedFilters([...filters])
    },
    castToFilter ( key, label, options = [], selected = null, type = 'check' ) {
      const id = SS360_FILTER_MAP[key]
      return {
        id,
        key,
        type,
        label,
        options,
        selected,
      }
    },
    castToFilterOption ( label = '', slug = '', count = 0 ) {
      return {
        label,
        slug,
        count,
      }
    },
    stripUnusedFilters (filters) {
      const appliedFilters = this.appliedFilters
      return [...filters.filter((filter) => {
        return appliedFilters.some((item) => {
          return item === filter.key
        })
      })]
    },

    /**
     * Event listener methods
     */
    onChangeApiData () {
      // Only update filters if we have different filters
      const newFilters = this.buildFilters()
      if (!this.isObjectEqual(this.filters, newFilters)) {
        this.filters = newFilters
      }
    },
    onChangeFilters () {
      // Only start new search if the selected filters have changed
      const filters = this.filters.reduce((acc, filter) => {
        if (filter.selected !== null) {
          const selectedFilter = {[(filter.key)]: filter.selected}
          return {...acc, ...selectedFilter}
        } else {
          return acc
        }
      }, {})
      const keyword = this.keyword ? {[this.keywordParameter]: this.keyword} : {}
      const query = {...keyword, ...filters}
      if (!this.isCurrentQuery(query)) {
        this.querySS360(this.keyword)
      }
    },
    onChangeRoute (route) {
      if (!this.isCurrentQuery(route.query)) {
        this.keyword = route.query.invoer

        // If keyword is empty, reset page
        if (this.keyword === "") {
          this.clearSearch()
        } else {
          this.querySS360(this.keyword)
        }
      }
    },
    onKeywordSubmit () {
      // If keyword is empty, reset page
      if (this.keyword === "") {
        this.clearSearch()
      } else {
        this.querySS360(this.keyword)
      }
    },
    onLoadMore () {
      // If keyword is empty, reset page
      if (this.keyword === "") {
        this.clearSearch()
      } else {
        this.querySS360(this.keyword, true)
      }
    },
    onToggleFilterDrawer (isOpen) {
      this.isFilterDrawerOpen = isOpen
    },

    /** 
     * ES6 utility methods
     */
    reverseMap (obj) {
      // Return reversed key values of an object
      return Object.keys(obj).reduce((acc, key) => {
        acc[obj[key]] = key;
        return acc;
      }, {});
    },
    isObjectEqual (...objects) {
      return objects.every(obj => JSON.stringify(obj) === JSON.stringify(objects[0]));
    },
    isCurrentQuery (query) {
      const { [this.keywordParameter]: keyword, ...filters } = this.$route.query
      const filterQueries = Object.entries(filters).reduce((acc, [ filterName, value ]) => {
        // Since the values of the filterQueries can be a String or an Array we need to TypeCast it to an Array
        return {...acc, ...{[filterName]: [value].flat()}}
      }, {})
      const uriQuery = {...{[this.keywordParameter]: keyword}, ...filterQueries}
      return this.isObjectEqual(uriQuery, query)
    },
  },
}
